import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useWishlist } from "../context/WishlistContext";
import { getShortName } from "../utils/productNames";
import { formatPrice } from "../utils/formatters";
import { toast } from "sonner";

export const ProductCard = ({ product, index = 0 }) => {
  const { isAuthenticated } = useAuth();
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const [imgIndex, setImgIndex] = useState(product.images?.length > 1 ? 1 : 0);

  const inWishlist = isInWishlist(product.id);
  const hasMultiple = product.images?.length > 1;

  const handleWishlistToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) { toast.error("Please login to add to wishlist"); return; }
    if (inWishlist) { await removeFromWishlist(product.id); toast.success("Removed from wishlist"); }
    else { await addToWishlist(product.id); toast.success("Added to wishlist"); }
  };

  const prevImg = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setImgIndex((i) => (i === 0 ? product.images.length - 1 : i - 1));
  };

  const nextImg = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setImgIndex((i) => (i === product.images.length - 1 ? 0 : i + 1));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`} className="block">
        <div className="card-brutalist overflow-hidden relative">
          <button
            onClick={handleWishlistToggle}
            className={`absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center border transition-colors ${
              inWishlist ? "bg-[#FFD700] border-[#FFD700] text-black" : "bg-black/50 border-white/20 text-white hover:border-[#FFD700] hover:text-[#FFD700]"
            }`}
            data-testid={`wishlist-btn-${product.id}`}
          >
            <Heart className={`w-5 h-5 ${inWishlist ? "fill-current" : ""}`} />
          </button>

          <div className="aspect-[3/4] overflow-hidden relative">
            <img
              src={product.images[imgIndex]}
              alt={product.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />

            {/* Arrows */}
            {hasMultiple && (
              <>
                <button
                  onClick={prevImg}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/60 border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#FFD700] hover:text-black"
                  data-testid={`card-prev-${product.id}`}
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={nextImg}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/60 border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#FFD700] hover:text-black"
                  data-testid={`card-next-${product.id}`}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </>
            )}

            {/* Dots */}
            {hasMultiple && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {product.images.map((_, idx) => (
                  <span key={idx} className={`w-2 h-2 rounded-full transition-colors ${idx === imgIndex ? "bg-[#FFD700]" : "bg-white/40"}`} />
                ))}
              </div>
            )}

            {/* Front/Back label */}
            {hasMultiple && (
              <div className="absolute top-4 left-4 bg-black/60 border border-white/20 px-2 py-1 font-mono text-[10px] tracking-wider text-white/80">
                {imgIndex === 0 ? "FRONT" : "BACK"}
              </div>
            )}
          </div>

          {product.featured && !hasMultiple && (
            <div className="absolute top-4 left-4 bg-[#FFD700] text-black px-3 py-1 font-mono text-xs tracking-widest uppercase">
              Featured
            </div>
          )}

          {product.stock === 0 && (
            <div className="absolute bottom-3 left-3">
              <span className="bg-black/80 border border-white/20 px-3 py-1 font-mono text-[10px] tracking-widest text-white/80">OUT OF STOCK</span>
            </div>
          )}
        </div>

        <div className="mt-4 space-y-2">
          <h3 className="font-heading text-xl tracking-wider group-hover:text-[#FFD700] transition-colors">
            {getShortName(product.name)}
          </h3>
          <p className="text-[#A3A3A3] text-sm line-clamp-2">{product.description.split('\n\n')[0]}</p>
          <div className="flex items-center justify-between">
            <p className="font-mono text-[#FFD700] text-lg">{formatPrice(product.price)}</p>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default ProductCard;
